// App-wide "embedded" flag for the embedded (cross-origin iframe) flow.
//
// The contract page can be opened inside an external page via the
// `?embeddedPage=true` query param. That param only lives on the initial URL and
// is dropped on later navigations/reloads, so we persist it to sessionStorage
// once (mirroring authTokenService) and expose it app-wide — letting the whole
// app (e.g. the main navigation sidebar) react to embedded mode, not just the
// contract page. sessionStorage is partitioned per-origin in third-party
// contexts and cleared when the frame closes.

const STORAGE_KEY = 'received_embedded';

// True when this document is inside a frame (the Stigg app's iframe). Unlike isEmbedded() this is
// not a styling flag and doesn't depend on `?embeddedPage=true`. Comparing window.top doesn't throw
// cross-origin (it's a WindowProxy); the catch is purely defensive.
export const isFramed = (win: Pick<Window, 'self' | 'top'> = window): boolean => {
	try {
		return win.self !== win.top;
	} catch {
		return true;
	}
};

// Mirror of the resolved value so callers avoid a sessionStorage read on every
// render, and so it still works if storage access throws.
let cachedEmbedded: boolean | undefined;

// Keep body[data-received] in lockstep with the embedded flag: embeddedTheme.scss is scoped to
// body[data-received='embedded'] and Select/MenuList/Modal portal to document.body. Toggle on every
// flag change (not just bootstrap) so mid-session setEmbedded/clearEmbedded stays consistent.
export const syncEmbeddedBodyAttr = (value: boolean) => {
	if (typeof document === 'undefined' || !document.body) {
		return;
	}
	if (value) {
		document.body.dataset.received = 'embedded';
	} else {
		delete document.body.dataset.received;
	}
};

export const setEmbedded = (value: boolean) => {
	cachedEmbedded = value;
	syncEmbeddedBodyAttr(value);
	try {
		sessionStorage.setItem(STORAGE_KEY, String(value));
	} catch {
		// sessionStorage can be unavailable/blocked; in-memory cache is enough for this session.
	}
};

export const isEmbedded = (): boolean => {
	if (cachedEmbedded === undefined) {
		// The `?embeddedPage=true` URL param is the iframe entry point: it wins on
		// first read and is persisted so the flag survives the param being dropped.
		// Resolve the URL signal before touching sessionStorage so a blocked/throwing
		// storage write (cross-origin third-party iframe) can't clobber a true URL
		// flag — mirroring how authTokenService caches in memory before persisting.
		const fromUrl = new URLSearchParams(window.location.search).get('embeddedPage') === 'true';
		let fromStorage = false;
		try {
			if (fromUrl) {
				sessionStorage.setItem(STORAGE_KEY, 'true');
			}
			fromStorage = sessionStorage.getItem(STORAGE_KEY) === 'true';
		} catch {
			// sessionStorage can be unavailable/blocked in a third-party iframe; the URL
			// flag alone is enough to resolve embedded mode for this frame session.
		}
		cachedEmbedded = fromUrl || fromStorage;
	}
	return cachedEmbedded;
};

export const clearEmbedded = () => {
	cachedEmbedded = false;
	syncEmbeddedBodyAttr(false);
	try {
		sessionStorage.removeItem(STORAGE_KEY);
	} catch {
		// nothing to clean up if storage is unavailable.
	}
};
